import { Link } from "react-router-dom";
import { Lock, ArrowRight } from "lucide-react";
import { useT } from "@/i18n/LanguageContext";

/**
 * TrialGate — shared pieces for the Academy public preview (2026-07).
 * Full lessons are studied inside My StoicOs; these components route every
 * study action into the EXISTING My StoicOs register/login flow. No new
 * auth or trial logic lives here. Copy = free introduction (no 7-day trial).
 */

export function TrialCta({ className = "", testid = "trial-cta", onClickTrack }) {
  const { t } = useT();
  return (
    <Link
      to="/my-stoicos"
      data-testid={testid}
      onClick={onClickTrack}
      className={className || "inline-flex items-center justify-center gap-3 min-h-[52px] px-8 py-4 bg-gold hover:bg-gold-hover text-ink text-[11px] tracking-[0.28em] uppercase font-semibold transition-all duration-500 shadow-[0_10px_40px_-10px_rgba(232,200,121,0.6)]"}
    >
      {t("trial.cta")}
      <ArrowRight size={14} strokeWidth={2} aria-hidden />
    </Link>
  );
}

export function LockBadge({ className = "" }) {
  return (
    <p className={`inline-flex items-center gap-1.5 text-[10px] tracking-[0.22em] uppercase text-bone/50 ${className}`} data-testid="lock-badge">
      <Lock size={11} strokeWidth={1.75} aria-hidden /> Study inside My StoicOs
    </p>
  );
}

export function LockedLesson({ discipline = "logic", backTo = "/academy", mode = "preview" }) {
  const { t } = useT();
  const intro = mode === "intro";
  return (
    <main className="min-h-screen bg-ink flex items-center justify-center px-6 py-24" data-testid={`${discipline}-locked-lesson`}>
      <div className="max-w-xl w-full text-center space-y-8">
        <div className="mx-auto w-14 h-14 border border-gold/40 flex items-center justify-center">
          <Lock size={22} strokeWidth={1.5} className="text-gold" aria-hidden />
        </div>
        <h1 className="font-serif text-2xl md:text-4xl text-bone leading-tight">
          {intro ? (
            t("trial.locked.title")
          ) : (
            <>This lesson is studied inside <span className="italic text-gold">My StoicOs</span></>
          )}
        </h1>
        <p className="text-base text-fog font-reading leading-[1.9] max-w-md mx-auto">
          {intro ? t("trial.locked.body") : t("trial.locked.previewBody")}
        </p>
        <div className="flex flex-col items-center gap-4">
          {intro ? (
            <Link to="/my-stoicos" data-testid={`${discipline}-locked-continue-link`}
              className="inline-flex items-center justify-center gap-3 min-h-[52px] px-8 py-4 bg-gold hover:bg-gold-hover text-ink text-[11px] tracking-[0.28em] uppercase font-semibold transition-all duration-500">
              {t("trial.locked.back")} <ArrowRight size={14} strokeWidth={2} aria-hidden />
            </Link>
          ) : (
            <>
              <TrialCta testid={`${discipline}-locked-trial-cta`} />
              <Link
                to="/my-stoicos?mode=login"
                data-testid={`${discipline}-locked-login-link`}
                className="text-sm text-bone/60 hover:text-gold underline underline-offset-4 transition-colors"
              >
                {t("trial.hero.secondary")}
              </Link>
            </>
          )}
          <Link to={backTo} className="text-[11px] tracking-[0.22em] uppercase text-bone/40 hover:text-bone/70 transition-colors">
            ← Back to the curriculum
          </Link>
        </div>
      </div>
    </main>
  );
}
